import { useLocation } from '@gatsbyjs/reach-router'
import React, { FC, useCallback } from 'react'
import styled, { css } from 'styled-components'

import { useSiteMetadataFrom } from '../../../../../../hooks/useSiteMetadata'
import useSiteSettings, { useSiteSettingsFrom } from '../../../../../../hooks/useSiteSettings'
import { useSiteSettingsAndMetadata } from '../../../../../../hooks/useSiteSettingsAndMetadata'
import { usePractitionerDocumentsNeeded } from '../../../../../../lib/practitioner-settings/hooks/usePractitionerDocumentsNeeded'
import { PractitionerSettings } from '../../../../../../lib/practitioner-settings/types'
import { defaultCommissionsOptOutLabel } from '../../../../../../lib/practitioner-settings/utils'
import { langCodes } from '../../../../../../utils/siteSettings'
import Spacer from '../../../../../Spacer'
import StatusAndSubmitDoc from '../../../settings/practitioner/StatusAndSubmitDoc'
import { MissingAccountInformationMessage } from './MissingAccountInformationMessage'

const StyledWrapper = styled.div<{ $isEScriptPage: boolean }>`
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  max-width: 100%;
  ${({ $isEScriptPage }) =>
    $isEScriptPage &&
    css`
      background-color: ${({ theme }) => theme.colors.coolGray.cool200};
      padding: 40px 20px;
    `};
  @media (min-width: 768px) {
    ${({ $isEScriptPage }) =>
      $isEScriptPage &&
      css`
        padding: 40px 60px;
      `};
  }
`

const StyledContent = styled.main`
  margin: 0 auto;
  width: 100%;
  max-width: 800px;
  margin-bottom: 40px;
`

const StyledHeader = styled.h2`
  font-family: ${({ theme }) => theme.fontFamilies.gotham};
  font-size: 24px;
  color: #001b36;
  margin-bottom: 8px;
`

const StyledSubheader = styled.p`
  && {
    font-family: ${({ theme }) => theme.fontFamilies.roboto};
    font-size: 16px;
    color: #001b36;
    margin-bottom: 32px;
  }
`

export interface DispensaryNotReadyMessagesProps {
  settings: PractitionerSettings
}

export const DispensaryNotReadyMessages: FC<DispensaryNotReadyMessagesProps> = ({ settings }) => {
  const location = useLocation()
  const getCurrentPath = useCallback(() => `${location.pathname}${location.search}`, [location])
  const isEScriptPage = getCurrentPath().includes('/escript/build')
  const siteSettingsAndMetadata = useSiteSettingsAndMetadata()
  const { lang } = useSiteSettings() || {}
  const { commissionsOptOutForm, ...siteSettings } = useSiteSettingsFrom(siteSettingsAndMetadata)
  const { featureFlags } = useSiteMetadataFrom(siteSettingsAndMetadata) ?? {}
  const { commissionsOptOutFormReceived } = settings.practitioner
  const { neededForCommissions } = usePractitionerDocumentsNeeded(
    settings.practitioner,
    siteSettings,
  )

  if (
    !featureFlags?.commissionsOptOut ||
    !neededForCommissions.length ||
    commissionsOptOutFormReceived
  ) {
    return (
      <p>
        Once your account is all set up, you will see information about your Virtual Dispensary
        right here. We are working on it for you right now!
      </p>
    )
  }

  return lang === langCodes.en_CA ? (
    <>
      <p>Submit either set of the following documents to activate your Virtual Dispensary.</p>
      <p>
        To activate your Virtual Dispensary and <b>start earning commissions</b>, submit the
        {neededForCommissions.length > 1 ? ' two documents' : ' document'} below!
      </p>
      {neededForCommissions.reduce((acc: React.ReactElement[], [key, { formTitle, formUrl }]) => {
        if (acc.length) {
          acc.push(<Spacer size={4} key={`${key}-spacer`} />)
        }
        acc.push(
          <StatusAndSubmitDoc
            formUrl={formUrl}
            isReceived={false}
            title={`${formTitle} Form`}
            key={key}
          />,
        )
        return acc
      }, [])}
      {!!commissionsOptOutForm?.link && (
        <>
          <Spacer size={4} />
          <p>
            To activate your Virtual Dispensary <b>without earning commissions</b>, submit the
            document below.
          </p>
          <StatusAndSubmitDoc
            formUrl={commissionsOptOutForm.link}
            isReceived={false}
            title={`${commissionsOptOutForm.label || defaultCommissionsOptOutLabel} Form`}
          />
        </>
      )}
    </>
  ) : (
    <StyledWrapper $isEScriptPage={isEScriptPage}>
      <StyledContent>
        <StyledHeader>Virtual Dispensary</StyledHeader>
        <StyledSubheader>
          Required forms for participation in rewards program
          {isEScriptPage && ', once your Dispensary is active you will be able to eScript.'}
        </StyledSubheader>
        <MissingAccountInformationMessage />
      </StyledContent>
    </StyledWrapper>
  )
}
